import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { Button, compressImage } from '@structural-climbing/ui'

interface Props {
  gymId: string
  imageUrl: string | null
  onLogoChange: (url: string) => void
}

const ACCEPTED = 'image/png,image/jpeg,image/webp,image/svg+xml'

export function LogoUpload({ gymId: _gymId, imageUrl, onLogoChange }: Props) {
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleFile(file: File) {
    setError(null)
    setUploading(true)
    try {
      const toUpload =
        file.type === 'image/svg+xml' ? file : await compressImage(file, 500 * 1024, 600)
      const token = await getAccessTokenSilently()
      const body = new FormData()
      body.append('file', toUpload)
      const res = await fetch(`${import.meta.env.VITE_API_URL as string}/gym-logo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body,
      })
      if (!res.ok) {
        const { error: msg } = (await res.json()) as { error?: string }
        setError(msg ?? t('common.uploadFailed'))
        return
      }
      const { url } = (await res.json()) as { url: string }
      onLogoChange(url)
    } catch {
      setError(t('common.uploadFailedRetry'))
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="flex items-center gap-6">
      <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-xl border border-divider bg-content2">
        {imageUrl ? (
          <img src={imageUrl} alt="Gym logo" className="h-full w-full object-cover" />
        ) : (
          <span className="text-xs text-foreground/40">{t('logo.noLogo')}</span>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) void handleFile(file)
            e.target.value = ''
          }}
        />
        <Button
          size="sm"
          variant="flat"
          isLoading={uploading}
          isDisabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          {imageUrl ? t('logo.changeLogo') : t('logo.uploadLogo')}
        </Button>
        <p className="text-xs text-foreground/40">{t('logo.hint')}</p>
        {error && <p className="text-xs text-danger">{error}</p>}
      </div>
    </div>
  )
}
